<template>
    <div>
        <div class="d-flex justify-content-end bg-white">
            <div class="pt-2 px-2 d-flex align-items-center justify-content-md-end">
                <p class="control mr-2 mt-2 mb-2">Résultats par page</p>
                <input
                    v-model="config.per_page"
                    type="number"
                    class="form-control w-25"
                    @keypress.enter="loadAsyncData"
                />
            </div>
            <div class="pt-2 px-2 d-flex align-items-center justify-content-md-end">
                <b-dropdown aria-role="list" :close-on-click="false" position="is-bottom-left">
                    <template #trigger="{ active }">
                        <b-button
                            :label="$t('global.columns')"
                            type="is-info"
                            class="mr-2"
                            :icon-right="active ? 'caret-up' : 'caret-down'"
                        />
                    </template>

                    <b-dropdown-item v-for="(col, index) in columns" :key="index" aria-role="listitem">
                        <b-field>
                            <b-checkbox v-model="col.active">
                                {{ col.name }}
                            </b-checkbox>
                        </b-field>
                    </b-dropdown-item>
                </b-dropdown>
            </div>
        </div>

        <b-table
            :data="writings.data"
            :default-sort="['id', 'desc']"
            :loading="loading"
            class="table-striped"
            :mobile-cards="true"
            :per-page="config.per_page"
            :show-detail-icon="true"
            :striped="true"
            :total="writings.total"
            backend-filtering
            backend-pagination
            backend-sorting
            paginated
            @sort="onSort"
            @page-change="onPageChange"
            @filters-change="onFilterChange"
        >
            <b-table-column :searchable="isSearchable" centered field="entity_type" label="Type" sortable width="0">
                <div slot="default" slot-scope="props">
                    <span v-if="props.row.entity_type === 1">Content</span>
                    <span v-else>Preorder</span>
                </div>

                <div slot="searchable" slot-scope="props">
                    <div class="w-100">
                        <select v-model="props.filters[props.column.field]" class="form-control form-control-sm">
                            <option :value="props.filters[props.column.field]">Tous</option>
                            <option value="1">Content</option>
                            <option value="2">Preorder</option>
                        </select>
                    </div>
                </div>
            </b-table-column>

            <b-table-column :searchable="isSearchable" centered field="entity_id" label="Ref" sortable width="0">
                <div slot="default" slot-scope="props">
                    <div class="tag" :class="[props.row.entity_type === 1 ? 'is-warning' : 'is-info']">
                        #{{ props.row.entity_id }}
                    </div>
                </div>

                <div slot="searchable" slot-scope="props">
                    <div class="w-100">
                        <input
                            v-model="props.filters[props.column.field]"
                            class="form-control form-control-sm"
                            placeholder="Réf..."
                            type="text"
                        />
                    </div>
                </div>
            </b-table-column>

            <b-table-column
                centered
                field="writing_status_id"
                label="Statut"
                searchable
                header-class="display-writing-status"
            >
                <div slot="default" slot-scope="props">
                    <span v-if="props.row.writing_status_id === 12">En cours d'envoi</span>
                    <span v-else>Terminé</span>
                </div>

                <div slot="searchable" slot-scope="props">
                    <div class="w-100">
                        <select v-model="props.filters[props.column.field]" class="form-control form-control-sm">
                            <option value="">&nbsp;</option>
                            <option value="12">En cours d'envoi</option>
                            <option value="6">Terminé</option>
                        </select>
                    </div>
                </div>
            </b-table-column>

            <b-table-column field="created_at" sortable :searchable="isSearchable" label="Créé le" centered>
                <div slot="default" slot-scope="props">
                    {{ moment(props.row.created_at).format('L') }}
                </div>

                <div slot="searchable" slot-scope="props">
                    <div class="w-100">
                        <input
                            v-model="props.filters[props.column.field]"
                            type="date"
                            class="form-control form-control-sm"
                            placeholder="Date..."
                        />
                    </div>
                </div>
            </b-table-column>

            <b-table-column field="written_at" sortable :searchable="isSearchable" label="Rédigé le" centered>
                <div slot="default" slot-scope="props">
                    {{ moment(props.row.written_at).format('L') }} à
                    {{ moment(props.row.written_at).format('HH:ss') }}
                </div>

                <div slot="searchable" slot-scope="props">
                    <div class="w-100">
                        <input
                            v-model="props.filters[props.column.field]"
                            type="date"
                            class="form-control form-control-sm"
                            placeholder="Date..."
                        />
                    </div>
                </div>
            </b-table-column>

            <b-table-column field="redactor" label="Rédigé par" searchable>
                <div slot="default" slot-scope="props">
                    <div v-if="props.row.redactor">
                        <p>
                            {{ props.row.redactor.user.firstname }}
                            {{ props.row.redactor.user.lastname }}
                        </p>
                        <p>
                            <span class="text-muted">{{ props.row.redactor.user.email }}</span>
                        </p>
                    </div>
                </div>
                <div slot="searchable" slot-scope="props">
                    <div class="w-100">
                        <input
                            v-model="props.filters['redactor']"
                            class="form-control form-control-sm"
                            placeholder="Nom..."
                            type="text"
                        />
                    </div>
                </div>
            </b-table-column>

            <b-table-column field="buyer.firstname" label="Client" searchable>
                <div slot="default" slot-scope="props">
                    <div v-if="props.row.entity_type === 1">
                        <p>
                            {{ props.row.related_order.buyer_firstname }}
                            {{ props.row.related_order.buyer_lastname }}<br />
                            <span class="text-muted">{{ props.row.related_order.buyer_email }}</span>
                        </p>
                    </div>
                    <div v-if="props.row.entity_type === 2">
                        <p v-if="props.row.related_order.campaign && props.row.related_order.campaign.project">
                            {{ props.row.related_order.campaign.project.name }}
                        </p>
                    </div>
                </div>
                <div slot="searchable" slot-scope="props">
                    <div class="w-100">
                        <input
                            v-model="props.filters['with_client']"
                            class="form-control form-control-sm"
                            placeholder="Client..."
                            type="text"
                        />
                    </div>
                </div>
            </b-table-column>

            <b-table-column field="partner.email" :searchable="isSearchable" :label="$t('partenaire-site.partner')">
                <div slot="default" slot-scope="props">
                    <span v-if="props.row.partner_id">
                        {{ props.row.partner_email }}
                        <LoginAs v-if="props.row.partner_id" :user-id="props.row.partner_id" class="ml-auto" />
                    </span>
                    <span v-else> Nextlevel </span>
                </div>

                <div slot="searchable" slot-scope="props">
                    <div class="w-100">
                        <input
                            v-model="props.filters['with_partner']"
                            class="form-control form-control-sm"
                            placeholder="Partenaire..."
                            type="text"
                        />
                    </div>
                </div>
            </b-table-column>

            <b-table-column :label="$t('redactor.bought-keywords')" :searchable="isSearchable" field="keyword">
                <div slot="default" slot-scope="props">
                    <div>
                        <p>{{ props.row.related_order.keyword }}</p>
                    </div>
                </div>

                <div slot="searchable" slot-scope="props">
                    <div class="w-100">
                        <input
                            v-model="props.filters[props.column.field]"
                            class="form-control form-control-sm"
                            placeholder="Mots clés..."
                            type="text"
                        />
                    </div>
                </div>
            </b-table-column>

            <b-table-column label="Url de publication" :searchable="isSearchable" field="page">
                <div slot="default" slot-scope="props">
                    <div>
                        <p>{{ props.row.page }}</p>
                    </div>
                </div>
            </b-table-column>

            <b-table-column
                field="lang"
                sortable
                :searchable="isSearchable"
                :label="$t('language.title')"
                :visible="columns.lang.active"
            >
                <div slot="default" slot-scope="props">
                    <p>
                        <flag :iso="props.row.lang == 'fr_fr' ? 'fr' : 'us'" />
                    </p>
                </div>

                <div slot="searchable" slot-scope="props">
                    <div class="w-100">
                        <input
                            v-model="props.filters[props.column.field]"
                            type="text"
                            class="form-control form-control-sm"
                            :placeholder="$t('language.title') + '...'"
                        />
                    </div>
                </div>
            </b-table-column>

            <b-table-column label="Actions">
                <div slot="default" slot-scope="props">
                    <div class="d-flex flex-column flex-md-row">
                        <router-link
                            :to="`/redactor/writing?id=${props.row.id}`"
                            class="btn btn-light"
                            title="Voir le contenu"
                        >
                            <i class="far fa-eye" />
                        </router-link>
                        <button
                            class="btn btn-light"
                            title="Copier le contenu"
                            @click.prevent="copyContent($event, props.row.writing_content)"
                        >
                            <i class="far fa-copy" />
                        </button>
                    </div>
                </div>
            </b-table-column>
        </b-table>

        <input ref="copyInput" type="hidden" />

        <RedactorConfirmModal ref="confirmModal" @unassignRedactorWriting="doUnassignOrder" />

        <RedactorReviewModal ref="reviewModal" />
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex'

    export default {
        name: 'WritingsInReviewTable',
        components: {},
        data: function () {
            return {
                search_field: '',
                loading: false,
                status: 'Tous',

                isSearchable: true,
                isMobileWidth: window.innerWidth < 768
            }
        },
        created() {},
        computed: {
            ...mapState('redactors', {
                writings: state => state.writings,
                config: state => state.writingsConfig,

                wordsRemainingToWrite: state => state.wordsRemainingToWrite,
                maxWordsPerDay: state => state.maxWordsPerDay,

                columns: state => state.columns
            }),
            dailyWordsCount() {
                return (
                    Math.round((parseInt(this.wordsRemainingToWrite) / parseInt(this.maxWordsPerDay)) * 100) / 100
                ).toFixed(1)
            }
        },
        methods: {
            ...mapActions('redactors', ['getWritingDashboard', 'setPriority', 'unassignWriting']),
            searchDate: function (item, inputStr) {
                var itemDate = this.moment(item.created_at).startOf('day').format('L')

                if (itemDate.includes(inputStr)) {
                    return true
                }

                return false
            },

            openConfirmModal(index, writing) {
                var data = {
                    index,
                    writing
                }
                this.$refs.confirmModal.openModal('processing', data)
            },

            doUnassignOrder(data) {
                this.loading = true
                this.unassignWriting(data.writing)
                    .then(() => {
                        var writing = this.writings.data[data.index]
                        writing.redactor = null
                        writing.redactor_id = null

                        this.writings.data.splice(index, 1, writing)
                    })
                    .catch(e => {
                        this.$store.dispatch('toast', {
                            type: 'error',
                            title: 'Une erreur est survenue',
                            message: e.response.message
                        })
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },

            setPrio: function (index, id, isPrio) {
                this.loading = true

                this.setPriority({ id: id, priority: isPrio })
                    .then(() => {
                        var writing = this.writings.data[index]
                        writing.isPrio = isPrio

                        this.writings.data.splice(index, 1, writing)
                    })
                    .catch(e => {
                        this.$store.dispatch('toast', {
                            type: 'error',
                            title: 'Une erreur est survenue',
                            message: e.response.message
                        })
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },

            togglePendingOrder(order) {
                if (order.pending_content) {
                    this.$emit('togglePendingOrder', { order: order, reason: null })
                } else {
                    this.$refs.confirmModal.openModal('pending', order)
                }
            },

            searchWordsCount(item, inputStr) {
                if (item.content.option_mots + 200 == inputStr) {
                    return true
                }

                return false
            },

            copyContent(e, content) {
                let testingCodeToCopy = this.$refs.copyInput
                testingCodeToCopy.setAttribute('type', 'text')

                testingCodeToCopy.value = content
                testingCodeToCopy.select()

                try {
                    document.execCommand('copy')
                    var btn = e.currentTarget

                    btn.classList.replace('btn-light', 'btn-info')
                    setTimeout(() => btn.classList.replace('btn-info', 'btn-light'), 2000)
                } catch (err) {
                    console.error('No copy ' + err)
                }
                testingCodeToCopy.setAttribute('type', 'hidden')
            },

            loadAsyncData() {
                this.loading = true
                this.getWritingDashboard('done').then(() => {
                    this.loading = false
                })
            },
            onSort(field, order) {
                this.config.sort = field
                this.config.sortDirection = order

                this.loadAsyncData()
            },
            onFilterChange(filter) {
                var filters = {}
                Object.keys(filter).forEach(element => {
                    filters[element] = filter[element]
                })
                this.config.filters = filters

                this.loadAsyncData()
            },
            onPageChange(page) {
                this.config.page = page

                this.loadAsyncData()
            }
        },
        mounted() {}
    }
</script>
